import * as React from 'react';
import { DefaultButton, DialogFooter, PrimaryButton } from '@fluentui/react';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { cloneDeep, isEqual } from 'lodash';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import { formStyles } from 'styles/formStyles';
import { Timestamps } from 'formComponents/Timestamps';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import {
    addArtikelDetail,
    ArtikelDetail,
    getArtikelDetail,
    korrekturArtikelDetail,
    updateArtikelDetail,
    useArtikelDetail,
} from 'api/artikelDetail';
import { ArtikelDetailHistorie, useArtikelDetailHistorieList } from 'api/artikelDetailHistorie';
import { viewStyles } from 'styles/viewStyles';
import AutoSizer from 'react-virtualized-auto-sizer';
import { List } from '@fluentui/react/lib/List';
import { HistoryActionShortLabel, RecordTypeShortLabel } from 'utils/labels';
import { createKundeUrl, createRecordUrl, formatDecimal, formatSmDateTime, parseDecimal } from 'utils/formatter';
import { useBetreuerByKz } from 'api/betreuer';
import { getArtikelDetailId, logDifference } from 'utils/helper';
import { getRecordOpenLink, OpenLink } from 'formComponents/OpenLink/OpenLink';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector } from 'store/lookups/selector';
import { TextField } from '@fluentui/react/lib/TextField';
import classnames from 'classnames';
import { LagerByIntNr } from 'formComponents/lookups/LagerByIntNr';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { ProduktgruppeByNr } from 'formComponents/lookups/ProduktgruppeByNr';
import { ProduzentByNr } from 'formComponents/lookups/ProduzentByNr';
import { ArtikelGruppeByNr } from 'formComponents/lookups/ArtikelGruppeByNr';
import { addArtikel, Artikel, getArtikel, useArtikel } from 'api/artikel';
import { useHistory } from 'react-router-dom';
import { addArtikelDetailLieferant, getArtikelDetailLieferantList } from 'api/artikelDetailLieferant';
import { getApiUrl } from 'api/useApi';
import { authSelector } from 'store/auth/selector';

const theme: ITheme = getTheme();
const { palette } = theme;

const styles = mergeStyleSets({
    container: {
        height: 580,
    },
    firstLine: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr max-content',
        gridColumnGap: 10,
    },
    secondLine: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 3fr',
        gridColumnGap: 10,
    },
    lagerstandLine: {
        display: 'grid',
        gridTemplateColumns: 'repeat(9, 1fr)',
        gridColumnGap: 10,
    },
    historieListContainer: {
        paddingTop: 10,
        height: 320,
    },
    historieLine: {
        display: 'grid',
        gridTemplateColumns: '3fr 2fr repeat(8, 1fr)',
        gridColumnGap: 10,
    },
    historyLineOdd: {
        background: palette.neutralLighter,
    },
    historyLineEven: {
        background: palette.white,
    },
    valuePair: {
        textAlign: 'right',
        paddingRight: 9,
    },
    changed: {
        color: '#000000',
        fontWeight: 700,
    },
    notChanged: {
        color: '#888888',
    },
    oldValue: {
        color: '#888888',
    },

    korrekturGrid: {
        marginTop: 20,
        marginBottom: 20,
        display: 'grid',
        gridTemplateColumns: '150px 120px 120px 120px',
        gridRowGap: 10,
        gridColumnGap: 10,
    },
    korrekturValueLabel: {
        textAlign: 'right',
        paddingRight: 9,
    },
    korrekturLabel: {
        marginTop: 7,
    },
    korrekturValue: {
        textAlign: 'right',
    },
    korrekturNote: {
        gridColumn: '2 / 5',
    },
    copyArtikelDetailContainer: {
        height: 350,
    },
    editRow: {
        display: 'grid',
        gridTemplateColumns: '250px 1fr',
        gridColumnGap: 10,
    },
});

interface Korrektur {
    error: string | null;
    newOffenBest: number;
    newOffenBestS: string;
    newOffenBestDifferenceS: string;
    newOffenBestD: number;
    newOffenBestDS: string;
    newOffenBestDDifferenceS: string;
    newOffenLief: number;
    newOffenLiefS: string;
    newOffenLiefDifferenceS: string;
    newOffenLiefD: number;
    newOffenLiefDS: string;
    newOffenLiefDDifferenceS: string;
    newBestand: number;
    newBestandS: string;
    newBestandDifferenceS: string;
    newOffenRes: number;
    newOffenResS: string;
    newOffenResDifferenceS: string;
    note: string;
}

interface CopyArtikelDetailData {
    show: boolean;
    artikelGruppeNr: number | null;
    artikelNr: string | null;
    saving: boolean;
    error: string | null;
}

const defaultCopyArtikelDetailData: CopyArtikelDetailData = {
    show: false,
    artikelGruppeNr: null,
    artikelNr: null,
    saving: false,
    error: null,
};

interface ArtikelDetailEditProps {
    artikelGruppeNr: number; // -1 = new
    artikelNr: string;
    refetch: () => void;
}

export const ArtikelDetailEdit: React.FC<ArtikelDetailEditProps> = ({ artikelGruppeNr, artikelNr, refetch }) => {
    const history = useHistory();
    const auth = useSelector(authSelector);
    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const isMounted = useIsMounted();
    const betreuerByKz = useBetreuerByKz();
    const artikelGruppeByNr = useSelector(artikelGruppenByNrSelector);
    const [korrektur, setKorrektur] = React.useState<Korrektur | null>(null);

    const [artikelDetail, setArtikelDetail] = React.useState<ArtikelDetail | null>(null);
    const [copyArtikelDetailData, setCopyArtikelDetailData] = React.useState<CopyArtikelDetailData>({
        ...defaultCopyArtikelDetailData,
    });

    const dbArtikelDetail = useArtikelDetail(
        {
            artikelGruppeNr,
            artikelNr,
            includeArchiv: true,
            withHauptlieferant: true,
            withDrehung: true,
        },
        {
            onSuccess: (data) => {
                setSavingState(SavingState.notChanged);
                setArtikelDetail(cloneDeep(data));
            },
        },
    );

    const dbArtikel = useArtikel({ artikelNr });

    const artikelDetailHistorieList = useArtikelDetailHistorieList({
        artikelGruppeNr,
        artikelNr,
    });

    React.useEffect(() => {
        if (
            savingState === SavingState.notChanged &&
            !dbArtikelDetail.isLoading &&
            !dbArtikelDetail.isFetching &&
            artikelDetail !== null &&
            !isEqual(dbArtikelDetail.data, artikelDetail)
        ) {
            setSavingState(SavingState.notSaved);
            logDifference('artikelDetail', artikelDetail, dbArtikelDetail);
        }
    }, [savingState, dbArtikelDetail, artikelDetail]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const save = () => {
        if (artikelDetail && savingState !== SavingState.saving) {
            setSavingState(SavingState.saving);
            if (artikelGruppeNr === -1) {
                addArtikelDetail(artikelDetail)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        if (isMounted) {
                            dbArtikelDetail.refetch();
                        }
                        refetch();
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            } else {
                updateArtikelDetail(artikelDetail)
                    .then(() => {
                        setSavingState(SavingState.saved);
                        if (isMounted) {
                            dbArtikelDetail.refetch();
                        }
                        refetch();
                    })
                    .catch((error) => {
                        console.error(error);
                        setSavingState(SavingState.error);
                    });
            }
        }
    };

    const renderArtikelGruppe = () => (
        <FormTextField
            value={artikelGruppeByNr[artikelGruppeNr]?.text}
            savingState={savingState}
            options={{ disabled: true }}
            onChange={(): void => {}}
            label="Artikelgruppe"
        />
    );

    const renderProduktgruppe = () => (
        <ProduktgruppeByNr
            value={artikelDetail?.produktGruppeNr || 0}
            disabled={savingState === SavingState.saving}
            label="Produktgruppe"
            allowEmpty
            menuPlacement="bottom"
            onChange={(newValue): void => {
                if (artikelDetail) {
                    setArtikelDetail({
                        ...artikelDetail,
                        produktGruppeNr: newValue,
                    });
                }
            }}
        />
    );

    const renderProduzent = () => (
        <ProduzentByNr
            value={artikelDetail?.produzentNr || 0}
            disabled={savingState === SavingState.saving}
            label="Produzent"
            allowEmpty
            menuPlacement="bottom"
            onChange={(newValue): void => {
                if (artikelDetail) {
                    setArtikelDetail({
                        ...artikelDetail,
                        produzentNr: newValue,
                    });
                }
            }}
        />
    );

    const renderLager = () => (
        <LagerByIntNr
            value={artikelDetail?.lagerIntNr || 0}
            disabled={savingState === SavingState.saving}
            label="Lager"
            allowEmpty
            menuPlacement="bottom"
            onChange={(newValue): void => {
                if (artikelDetail) {
                    setArtikelDetail({
                        ...artikelDetail,
                        lagerIntNr: newValue,
                    });
                }
            }}
        />
    );

    const renderTextField = (column: keyof ArtikelDetail, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(artikelDetail?.[column] || '') as string}
            savingState={savingState}
            options={options}
            onChange={(newValue): void => {
                if (artikelDetail) {
                    setArtikelDetail({
                        ...artikelDetail,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (column: keyof ArtikelDetail, label: string, options: FormDecimalFieldOptions = {}) => (
        <FormDecimalField
            value={(artikelDetail?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled }}
            onChange={(newValue): void => {
                if (options.onChange) {
                    options.onChange(newValue);
                } else if (artikelDetail) {
                    setArtikelDetail({
                        ...artikelDetail,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderVerfuegbar = () => (
        <FormDecimalField
            value={(artikelDetail?.bestand || 0) - (artikelDetail?.offenRes || 0)}
            savingState={savingState}
            options={{ disabled: true, fixed: 4 }}
            onChange={(): void => {}}
            label="Verfügbar"
        />
    );

    const renderCheckbox = (column: keyof ArtikelDetail, label: string, className?: string) => (
        <Checkbox
            className={className || formStyles.checkboxInsideForm}
            label={label}
            disabled={savingState === SavingState.saving}
            checked={artikelDetail?.[column] as boolean}
            onChange={(event, checked) => {
                if (artikelDetail) {
                    setArtikelDetail({
                        ...artikelDetail,
                        [column]: checked || false,
                    });
                }
            }}
        />
    );

    const renderValuePair = React.useCallback(
        (oldValue: number, newValue: number) => (
            <div className={styles.valuePair}>
                <div className={oldValue === newValue ? styles.notChanged : styles.changed}>
                    {formatDecimal(newValue, 4)}
                </div>
                {oldValue !== newValue && <div className={styles.oldValue}>{formatDecimal(oldValue, 4)}</div>}
            </div>
        ),
        [],
    );

    const listRef = React.useRef<List<ArtikelDetailHistorie>>(null);

    const onRenderCell = React.useCallback(
        (item: ArtikelDetailHistorie | undefined, index: number | undefined): JSX.Element => {
            if (!item) {
                return <div />;
            }

            const kundeLabel = item.kNr
                ? (item.kName1 || '') + ' ' + (item.kName2 || '') + (item.kNr ? `(${item.kNr})` : '')
                : '';

            return (
                <div
                    className={classnames(
                        styles.historieLine,
                        (index || 0) % 2 ? styles.historyLineEven : styles.historyLineOdd,
                    )}
                >
                    <div>
                        <div>
                            {formatSmDateTime(item.anlageDat, item.anlageUhr)}{' '}
                            {item.anlageUser
                                ? betreuerByKz[item.anlageUser]
                                    ? betreuerByKz[item.anlageUser].name || item.anlageUser
                                    : item.anlageUser
                                : ''}
                        </div>
                        <div title={kundeLabel}>
                            {item.kNr && <OpenLink link={createKundeUrl(item.kNr)} label={kundeLabel} />}
                        </div>
                    </div>
                    <div>
                        <div>
                            {item.recordType &&
                                item.recordNr &&
                                getRecordOpenLink(
                                    item.recordType,
                                    item.recordNr,
                                    item.kNr,
                                    undefined,
                                    (RecordTypeShortLabel[item.recordType] || item.recordType) + ' ' + item.recordNr,
                                )}
                        </div>
                        <div>
                            <div className={styles.changed}>{HistoryActionShortLabel[item.action] || item.action}</div>
                            {item.note && <div>{item.note}</div>}
                        </div>
                    </div>
                    <div className={styles.valuePair}>
                        <div>
                            <span className={item.oldLArt === item.newLArt ? styles.notChanged : styles.changed}>
                                {item.newLArt}
                            </span>
                            <span className={styles.notChanged}>/</span>
                            <span className={item.oldMenge === item.newMenge ? styles.notChanged : styles.changed}>
                                {formatDecimal(item.newMenge, 4)}
                            </span>
                        </div>
                        {(item.oldLArt !== item.newLArt || item.oldMenge !== item.newMenge) && (
                            <div className={styles.oldValue}>
                                {item.oldLArt + '/' + formatDecimal(item.oldMenge, 4)}
                            </div>
                        )}
                    </div>
                    {renderValuePair(item.oldOffenLief, item.newOffenLief)}
                    {renderValuePair(item.oldOffenLiefD, item.newOffenLiefD)}
                    {renderValuePair(item.oldOffenBest, item.newOffenBest)}
                    {renderValuePair(item.oldOffenBestD, item.newOffenBestD)}
                    {renderValuePair(item.oldBestand, item.newBestand)}
                    {renderValuePair(item.oldOffenRes, item.newOffenRes)}
                    {renderValuePair(item.oldBestand - item.oldOffenRes, item.newBestand - item.newOffenRes)}
                </div>
            );
        },
        [betreuerByKz, renderValuePair],
    );

    return (
        <div className={styles.container}>
            {routerConfirmMessage.render()}

            {(dbArtikelDetail.isLoading ||
                dbArtikelDetail.isFetching ||
                dbArtikel.isLoading ||
                dbArtikel.isFetching) && <LoadingIndicator />}
            {artikelDetail && (
                <form className={formStyles.form}>
                    <div className={styles.firstLine}>
                        <div className={formStyles.gridCell}>{renderArtikelGruppe()}</div>
                        <div className={formStyles.gridCell}>
                            {renderTextField('artikelNr', 'Artikel-Nr.', { disabled: true })}
                        </div>
                        <div className={formStyles.gridCell}>
                            {renderTextField('lagerArtikelNr', 'Artikel-Nr. - Lager', { disabled: true })}
                        </div>
                        <div className={formStyles.gridCell}>
                            <FormTextField
                                value={artikelDetail?.hauptlieferant?.lieferantArtikelNr || ''}
                                savingState={savingState}
                                options={{
                                    disabled: true,
                                }}
                                onChange={() => {}}
                                label="Artikel-Nr. - Lieferant"
                            />
                        </div>
                        <div className={formStyles.gridCell}>{renderTextField('nrKunde', 'Artikel-Nr. - Kunde')}</div>
                        <div className={formStyles.gridCell}>{renderLager()}</div>
                        <div className={formStyles.gridCellRight}>
                            {renderCheckbox('archiv', 'Archiv', formStyles.checkboxInsideFormAligned)}
                        </div>
                    </div>
                    <div className={styles.secondLine}>
                        <div className={formStyles.gridCell}>{renderProduktgruppe()}</div>
                        <div className={formStyles.gridCell}>{renderProduzent()}</div>
                        <div className={formStyles.sixColumnsBlock}>
                            <div className={formStyles.gridCell}>
                                {renderTextField('bestZeit', 'Lieferzeit (Tage)', { numeric: true })}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderDecimalField('minBestand', 'Mindestbestand', {
                                    fixed: 4,
                                })}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderDecimalField('minBestMenge', 'Min.Best.Menge', {
                                    fixed: 4,
                                })}
                            </div>
                            <div className={formStyles.gridCell}>
                                {renderDecimalField('drehung', 'Drehung (12 Mon)', {
                                    fixed: 4,
                                    disabled: true,
                                })}
                            </div>
                            <div className={formStyles.gridCell}>{renderTextField('regal', 'Regal')}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextField('fach', 'Fach', { numeric: true })}
                            </div>
                        </div>
                    </div>
                    <div className={styles.historieLine}>
                        <div />
                        <div />
                        <div />
                        <div className={formStyles.gridCell}>
                            {renderDecimalField('offenLief', 'Offene Lief.', {
                                fixed: 4,
                                disabled: true,
                            })}
                        </div>
                        <div className={formStyles.gridCell}>
                            {renderDecimalField('offenLiefD', 'Offene Lief. (D)', {
                                fixed: 4,
                                disabled: true,
                            })}
                        </div>
                        <div className={formStyles.gridCell}>
                            {renderDecimalField('offenBest', 'Offene Best.', {
                                fixed: 4,
                                disabled: true,
                            })}
                        </div>
                        <div className={formStyles.gridCell}>
                            {renderDecimalField('offenBestD', 'Offene Best. (D)', {
                                fixed: 4,
                                disabled: true,
                            })}
                        </div>
                        <div className={formStyles.gridCell}>
                            {renderDecimalField('bestand', 'Bestand', { fixed: 4, disabled: true })}
                        </div>
                        <div className={formStyles.gridCell}>
                            {renderDecimalField('offenRes', 'Reservierungen', {
                                fixed: 4,
                                disabled: true,
                            })}
                        </div>
                        <div className={formStyles.gridCell}>{renderVerfuegbar()}</div>
                    </div>

                    <div className={styles.historieListContainer}>
                        {!artikelDetailHistorieList.isLoading &&
                            !artikelDetailHistorieList.isFetching &&
                            (artikelDetailHistorieList?.data && artikelDetailHistorieList?.data.length === 0 ? (
                                <div className={viewStyles.noData}>Keine Historie.</div>
                            ) : (
                                <AutoSizer>
                                    {({ height, width }) => (
                                        <div
                                            style={{ width, height, overflowX: 'hidden', overflowY: 'scroll' }}
                                            onScroll={() => {
                                                // update doesn't trigger automatically inside a dialog (probably a bug)
                                                listRef.current?.forceUpdate();
                                            }}
                                        >
                                            <List
                                                ref={listRef}
                                                items={artikelDetailHistorieList.data || []}
                                                onRenderCell={onRenderCell}
                                            />
                                        </div>
                                    )}
                                </AutoSizer>
                            ))}
                    </div>

                    <Timestamps
                        createdDate={artikelDetail?.anlageDat}
                        createdDecimalTime={artikelDetail?.anlageUhr}
                        createdUser={artikelDetail?.anlageUser}
                        modifiedDate={artikelDetail.updDat}
                        modifiedDecimalTime={artikelDetail.updUhr}
                        modifiedUser={artikelDetail.updUser}
                    />

                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}
                    <DialogFooter>
                        <DefaultButton
                            aria-roledescription="action button"
                            disabled={
                                savingState !== SavingState.notChanged ||
                                dbArtikelDetail.isLoading ||
                                dbArtikel.isLoading ||
                                auth.rolle === 'EX'
                            }
                            onClick={() =>
                                window.open(
                                    getApiUrl('artikelDetail/pdf') +
                                        '?artikelGruppeNr=' +
                                        artikelDetail?.artikelGruppeNr +
                                        '&artikelNr=' +
                                        artikelDetail?.artikelNr,
                                )
                            }
                            text="Label drucken"
                        />
                        <DefaultButton
                            className={formStyles.copyButton}
                            onClick={() =>
                                setCopyArtikelDetailData({
                                    ...defaultCopyArtikelDetailData,
                                    show: true,
                                    artikelNr: artikelDetail?.artikelNr || '',
                                    artikelGruppeNr: artikelDetail?.artikelGruppeNr,
                                })
                            }
                            text="Kopieren"
                            disabled={
                                savingState !== SavingState.notChanged ||
                                dbArtikelDetail.isLoading ||
                                dbArtikel.isLoading ||
                                auth.rolle === 'EX'
                            }
                        />
                        <PrimaryButton
                            onClick={() => {
                                if (artikelDetail) {
                                    setKorrektur({
                                        error: null,
                                        newOffenBest: artikelDetail.offenBest,
                                        newOffenBestS: formatDecimal(artikelDetail.offenBest, 4),
                                        newOffenBestDifferenceS: formatDecimal(0, 4),
                                        newOffenBestD: artikelDetail.offenBestD,
                                        newOffenBestDS: formatDecimal(artikelDetail.offenBestD, 4),
                                        newOffenBestDDifferenceS: formatDecimal(0, 4),
                                        newOffenLief: artikelDetail.offenLief,
                                        newOffenLiefS: formatDecimal(artikelDetail.offenLief, 4),
                                        newOffenLiefDifferenceS: formatDecimal(0, 4),
                                        newOffenLiefD: artikelDetail.offenLiefD,
                                        newOffenLiefDS: formatDecimal(artikelDetail.offenLiefD, 4),
                                        newOffenLiefDDifferenceS: formatDecimal(0, 4),
                                        newOffenRes: artikelDetail.offenRes,
                                        newOffenResS: formatDecimal(artikelDetail.offenRes, 4),
                                        newOffenResDifferenceS: formatDecimal(0, 4),
                                        newBestand: artikelDetail.bestand,
                                        newBestandS: formatDecimal(artikelDetail.bestand, 4),
                                        newBestandDifferenceS: formatDecimal(0, 4),
                                        note: '',
                                    });
                                }
                            }}
                            text="Lagerdaten korrigieren"
                            disabled={
                                savingState !== SavingState.notChanged ||
                                dbArtikelDetail.isLoading ||
                                dbArtikel.isLoading ||
                                auth.rolle === 'EX'
                            }
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton onClick={save} text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={save}
                                text="Speichern"
                                disabled={
                                    savingState === SavingState.notChanged ||
                                    dbArtikelDetail.isLoading ||
                                    dbArtikel.isLoading ||
                                    auth.rolle === 'EX'
                                }
                            />
                        )}
                    </DialogFooter>
                </form>
            )}
            {artikelDetail && korrektur !== null && (
                <ConfirmMessage
                    title={`Lagerdaten des Artikels 
                    ${artikelGruppeByNr[artikelGruppeNr]?.text} | ${artikelNr} ändern`}
                    message=""
                    confirmText="Lagerdaten korrigieren"
                    cancelText="Abbruch"
                    minWidth={650}
                    content={
                        <div>
                            <div className={styles.korrekturGrid}>
                                <div />
                                <div className={styles.korrekturValueLabel}>Aktuell</div>
                                <div className={styles.korrekturValueLabel}>Neu</div>
                                <div className={styles.korrekturValueLabel}>Differenz</div>

                                <div className={styles.korrekturLabel}>Offene Bestellungen</div>
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    readOnly
                                    disabled
                                    value={formatDecimal(artikelDetail.offenBest || 0, 4)}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    value={korrektur.newOffenBestS}
                                    onChange={(event, newValue) =>
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenBestS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        const decimalValue = parseDecimal(event.target.value || '') || 0;
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenBest: decimalValue,
                                            newOffenBestS: formatDecimal(decimalValue, 4),
                                            newOffenBestDifferenceS: formatDecimal(
                                                decimalValue - artikelDetail.offenBest,
                                                4,
                                            ),
                                        });
                                    }}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    value={korrektur.newOffenBestDifferenceS}
                                    onChange={(event, newValue) =>
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenBestDifferenceS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        const decimalValue = parseDecimal(event.target.value || '') || 0;
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenBest: artikelDetail.offenBest + decimalValue,
                                            newOffenBestS: formatDecimal(artikelDetail.offenBest + decimalValue, 4),
                                            newOffenBestDifferenceS: formatDecimal(decimalValue, 4),
                                        });
                                    }}
                                />

                                <div className={styles.korrekturLabel}>Offene Bestellungen (D)</div>
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    readOnly
                                    disabled
                                    value={formatDecimal(artikelDetail.offenBestD || 0, 4)}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    value={korrektur.newOffenBestDS}
                                    onChange={(event, newValue) =>
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenBestDS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        const decimalValue = parseDecimal(event.target.value || '') || 0;
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenBestD: decimalValue,
                                            newOffenBestDS: formatDecimal(decimalValue, 4),
                                            newOffenBestDDifferenceS: formatDecimal(
                                                decimalValue - artikelDetail.offenBestD,
                                                4,
                                            ),
                                        });
                                    }}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    value={korrektur.newOffenBestDDifferenceS}
                                    onChange={(event, newValue) =>
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenBestDDifferenceS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        const decimalValue = parseDecimal(event.target.value || '') || 0;
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenBestD: artikelDetail.offenBestD + decimalValue,
                                            newOffenBestDS: formatDecimal(artikelDetail.offenBestD + decimalValue, 4),
                                            newOffenBestDDifferenceS: formatDecimal(decimalValue, 4),
                                        });
                                    }}
                                />

                                <div className={styles.korrekturLabel}>Offene Lieferungen</div>
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    readOnly
                                    disabled
                                    value={formatDecimal(artikelDetail.offenLief || 0, 4)}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    value={korrektur.newOffenLiefS}
                                    onChange={(event, newValue) =>
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenLiefS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        const decimalValue = parseDecimal(event.target.value || '') || 0;
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenLief: decimalValue,
                                            newOffenLiefS: formatDecimal(decimalValue, 4),
                                            newOffenLiefDifferenceS: formatDecimal(
                                                decimalValue - artikelDetail.offenLief,
                                                4,
                                            ),
                                        });
                                    }}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    value={korrektur.newOffenLiefDifferenceS}
                                    onChange={(event, newValue) =>
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenLiefDifferenceS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        const decimalValue = parseDecimal(event.target.value || '') || 0;
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenLief: artikelDetail.offenLief + decimalValue,
                                            newOffenLiefS: formatDecimal(artikelDetail.offenLief + decimalValue, 4),
                                            newOffenLiefDifferenceS: formatDecimal(decimalValue, 4),
                                        });
                                    }}
                                />

                                <div className={styles.korrekturLabel}>Offene Lieferungen (D)</div>
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    readOnly
                                    disabled
                                    value={formatDecimal(artikelDetail.offenLiefD || 0, 4)}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    value={korrektur.newOffenLiefDS}
                                    onChange={(event, newValue) =>
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenLiefDS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        const decimalValue = parseDecimal(event.target.value || '') || 0;
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenLiefD: decimalValue,
                                            newOffenLiefDS: formatDecimal(decimalValue, 4),
                                            newOffenLiefDDifferenceS: formatDecimal(
                                                decimalValue - artikelDetail.offenLiefD,
                                                4,
                                            ),
                                        });
                                    }}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    value={korrektur.newOffenLiefDDifferenceS}
                                    onChange={(event, newValue) =>
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenLiefDDifferenceS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        const decimalValue = parseDecimal(event.target.value || '') || 0;
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenLiefD: artikelDetail.offenLiefD + decimalValue,
                                            newOffenLiefDS: formatDecimal(artikelDetail.offenLiefD + decimalValue, 4),
                                            newOffenLiefDDifferenceS: formatDecimal(decimalValue, 4),
                                        });
                                    }}
                                />

                                <div className={styles.korrekturLabel}>Offene Reservierungen</div>
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    readOnly
                                    disabled
                                    value={formatDecimal(artikelDetail.offenRes || 0, 4)}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    value={korrektur.newOffenResS}
                                    onChange={(event, newValue) =>
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenResS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        const decimalValue = parseDecimal(event.target.value || '') || 0;
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenRes: decimalValue,
                                            newOffenResS: formatDecimal(decimalValue, 4),
                                            newOffenResDifferenceS: formatDecimal(
                                                decimalValue - artikelDetail.offenRes,
                                                4,
                                            ),
                                        });
                                    }}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    value={korrektur.newOffenResDifferenceS}
                                    onChange={(event, newValue) =>
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenResDifferenceS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        const decimalValue = parseDecimal(event.target.value || '') || 0;
                                        setKorrektur({
                                            ...korrektur,
                                            newOffenRes: artikelDetail.offenRes + decimalValue,
                                            newOffenResS: formatDecimal(artikelDetail.offenRes + decimalValue, 4),
                                            newOffenResDifferenceS: formatDecimal(decimalValue, 4),
                                        });
                                    }}
                                />

                                <div className={styles.korrekturLabel}>Bestand</div>
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    readOnly
                                    disabled
                                    value={formatDecimal(artikelDetail.bestand || 0, 4)}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    value={korrektur.newBestandS}
                                    onChange={(event, newValue) =>
                                        setKorrektur({
                                            ...korrektur,
                                            newBestandS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        const decimalValue = parseDecimal(event.target.value || '') || 0;
                                        setKorrektur({
                                            ...korrektur,
                                            newBestand: decimalValue,
                                            newBestandS: formatDecimal(decimalValue, 4),
                                            newBestandDifferenceS: formatDecimal(
                                                decimalValue - artikelDetail.bestand,
                                                4,
                                            ),
                                        });
                                    }}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    value={korrektur.newBestandDifferenceS}
                                    onChange={(event, newValue) =>
                                        setKorrektur({
                                            ...korrektur,
                                            newBestandDifferenceS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        const decimalValue = parseDecimal(event.target.value || '') || 0;
                                        setKorrektur({
                                            ...korrektur,
                                            newBestand: artikelDetail.bestand + decimalValue,
                                            newBestandS: formatDecimal(artikelDetail.bestand + decimalValue, 4),
                                            newBestandDifferenceS: formatDecimal(decimalValue, 4),
                                        });
                                    }}
                                />

                                <div className={styles.korrekturLabel}>Verfügbar</div>
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    readOnly
                                    disabled
                                    value={formatDecimal(artikelDetail.bestand - artikelDetail.offenRes, 4)}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    readOnly
                                    disabled
                                    value={formatDecimal(korrektur.newBestand - korrektur.newOffenRes, 4)}
                                />
                                <TextField
                                    inputClassName={styles.korrekturValue}
                                    readOnly
                                    disabled
                                    value={formatDecimal(
                                        korrektur.newBestand -
                                            korrektur.newOffenRes -
                                            (artikelDetail.bestand - artikelDetail.offenRes),
                                        4,
                                    )}
                                />

                                <div className={styles.korrekturLabel}>Anmerkung</div>
                                <div className={styles.korrekturNote}>
                                    <TextField
                                        value={korrektur.note}
                                        onChange={(event, newValue) =>
                                            setKorrektur({
                                                ...korrektur,
                                                note: newValue === undefined ? '' : newValue,
                                            })
                                        }
                                    />
                                </div>
                                {korrektur.error && <div className={formStyles.error}>{korrektur.error}</div>}
                            </div>
                        </div>
                    }
                    onCancel={() => setKorrektur(null)}
                    onConfirm={async () => {
                        if (korrektur) {
                            const result = await korrekturArtikelDetail(
                                {
                                    ...artikelDetail,
                                    offenBest: korrektur.newOffenBest,
                                    offenBestD: korrektur.newOffenBestD,
                                    offenLief: korrektur.newOffenLief,
                                    offenLiefD: korrektur.newOffenLiefD,
                                    offenRes: korrektur.newOffenRes,
                                    bestand: korrektur.newBestand,
                                },
                                korrektur.note,
                            );

                            if (!result || result.error) {
                                setKorrektur({
                                    ...korrektur,
                                    error: 'Fehler beim Speichern',
                                });
                            } else {
                                setKorrektur(null);
                            }

                            dbArtikelDetail.refetch().then();
                            artikelDetailHistorieList.refetch().then();
                        }
                    }}
                />
            )}
            {copyArtikelDetailData.show && (
                <ConfirmMessage
                    title="Artikel kopieren"
                    message="Ein Artikel wird vollständig oder in ein neue Artikelgruppe kopiert."
                    minWidth={800}
                    confirmText="Artikel kopieren"
                    confirmDisabled={
                        copyArtikelDetailData.artikelGruppeNr === defaultCopyArtikelDetailData.artikelGruppeNr ||
                        copyArtikelDetailData.artikelNr === defaultCopyArtikelDetailData.artikelNr
                    }
                    cancelText="Abbruch"
                    content={
                        <div className={styles.copyArtikelDetailContainer}>
                            <ul>
                                <li>
                                    Wenn noch kein Artikel mit der angegebenen Nummer existiert, wird dieser mit den
                                    Daten des aktuellen Artikels angelegt.
                                </li>
                                <li>
                                    Existiert bereits ein Artikel mit dieser Nummer, wird dieser nur mit der angegebenen
                                    Artikelgruppe verknüpft.
                                </li>
                            </ul>
                            <div className={styles.editRow}>
                                <div>
                                    <ArtikelGruppeByNr
                                        value={copyArtikelDetailData.artikelGruppeNr}
                                        disabled={false}
                                        label="Artikelgruppe"
                                        renderLabel="label"
                                        onChange={(newValue): void => {
                                            setCopyArtikelDetailData({
                                                ...copyArtikelDetailData,
                                                artikelGruppeNr: newValue,
                                            });
                                        }}
                                    />
                                </div>
                                <TextField
                                    label="Artikel-Nr."
                                    value={copyArtikelDetailData.artikelNr || ''}
                                    onChange={(event, newValue): void => {
                                        setCopyArtikelDetailData({
                                            ...copyArtikelDetailData,
                                            artikelNr: newValue || '',
                                        });
                                    }}
                                />
                            </div>
                            {copyArtikelDetailData.error && (
                                <div className={formStyles.error}>{copyArtikelDetailData.error}</div>
                            )}
                        </div>
                    }
                    onConfirm={async () => {
                        if (copyArtikelDetailData.saving || !dbArtikel.data || !dbArtikelDetail.data) {
                            return;
                        }

                        if (!copyArtikelDetailData.artikelGruppeNr || !copyArtikelDetailData.artikelNr) {
                            setCopyArtikelDetailData({
                                ...copyArtikelDetailData,
                                saving: false,
                            });
                            return;
                        }

                        setCopyArtikelDetailData({
                            ...copyArtikelDetailData,
                            saving: true,
                            error: null,
                        });

                        const newArtikelGruppeNr = copyArtikelDetailData.artikelGruppeNr;
                        const newArtikelNr = copyArtikelDetailData.artikelNr.trim();

                        let copyArtikel: Artikel | null;
                        try {
                            copyArtikel = await getArtikel({ artikelNr: newArtikelNr });
                            console.info('-> artikel found');
                        } catch {
                            copyArtikel = null;
                            console.info('-> artikel not found');
                        }

                        if (!copyArtikel) {
                            copyArtikel = {
                                ...dbArtikel.data,
                                nr: newArtikelNr,
                            };

                            try {
                                await addArtikel(copyArtikel);
                            } catch {
                                setCopyArtikelDetailData({
                                    ...copyArtikelDetailData,
                                    saving: false,
                                    error: 'Fehler beim Speichern (Artikel).',
                                });
                            }
                        }

                        let copyArtikelDetail: ArtikelDetail | null = null;
                        try {
                            copyArtikelDetail = await getArtikelDetail({
                                artikelGruppeNr: newArtikelGruppeNr,
                                artikelNr: newArtikelNr,
                            });
                            console.info('-> artikelDetail found');
                        } catch {
                            copyArtikel = null;
                            console.info('-> artikelDetail not found');
                        }

                        if (copyArtikelDetail) {
                            setCopyArtikelDetailData({
                                ...copyArtikelDetailData,
                                saving: false,
                                error: 'Dieser Artikel existiert in dieser Gruppe.',
                            });
                            return;
                        }

                        copyArtikelDetail = {
                            ...dbArtikelDetail.data,
                            artikelGruppeNr: newArtikelGruppeNr,
                            artikelNr: newArtikelNr,
                            offenLief: 0,
                            offenLiefD: 0,
                            offenBest: 0,
                            offenBestD: 0,
                            bestand: 0,
                            offenRes: 0,
                        };

                        try {
                            await addArtikelDetail(copyArtikelDetail);
                        } catch {
                            setCopyArtikelDetailData({
                                ...copyArtikelDetailData,
                                saving: false,
                                error: 'Fehler beim Speichern (ArtikelDetail).',
                            });
                            return;
                        }

                        try {
                            const artikelDetailLieferanten = await getArtikelDetailLieferantList({
                                artikelNr: dbArtikelDetail.data.artikelNr,
                                artikelGruppeNr: dbArtikelDetail.data.artikelGruppeNr,
                            });
                            // eslint-disable-next-line no-restricted-syntax
                            for (const artikelDetailLieferant of artikelDetailLieferanten) {
                                if (artikelDetailLieferant.art) {
                                    const newArtikelDetailLieferant = {
                                        ...artikelDetailLieferant,
                                        artikelNr: newArtikelNr,
                                        artikelGruppeNr: newArtikelGruppeNr,
                                    };

                                    // eslint-disable-next-line no-await-in-loop
                                    await addArtikelDetailLieferant(newArtikelDetailLieferant);
                                }
                            }
                        } catch {
                            setCopyArtikelDetailData({
                                ...copyArtikelDetailData,
                                saving: false,
                                error: 'Fehler beim Kopieren der Kalkulation.',
                            });
                            return;
                        }

                        refetch();
                        setCopyArtikelDetailData({ ...defaultCopyArtikelDetailData });

                        const id = getArtikelDetailId(newArtikelGruppeNr, newArtikelNr);
                        history.push(createRecordUrl('lager/artikel', undefined, id, undefined, false));
                    }}
                    onCancel={() => setCopyArtikelDetailData({ ...defaultCopyArtikelDetailData })}
                />
            )}
        </div>
    );
};
